import { useCallback, useMemo, useState } from "react";
import { Button, Flex, Label, Text } from "@jsq/ds/core";
import { useFormContext } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import { createAddressFields } from "../../constants";
import { FIELD_RENDERERS } from "../DynamicField/fieldRenderers";
import * as styles from "./styles_vanilla.css";
import type {
  ControllingPersonEntry,
  FormFieldConfig,
  SelectOption,
  TaxFormValues,
} from "../../types";

type ControllingPersonsFormProps = {
  fieldName: string;
  index: number;
  onSubmit: () => void | Promise<void>;
  onCancel: () => void;
  isNewEntry: boolean;
  infoText: string;
  addButtonText: string;
  updateButtonText: string;
  controllingPersonTypeOptions?: SelectOption[];
  showDateOfBirth?: boolean;
  controllingPersonTypeLabel?: string;
};

export const ControllingPersonsForm = ({
  fieldName,
  index,
  onSubmit,
  onCancel,
  isNewEntry,
  infoText,
  addButtonText,
  updateButtonText,
  controllingPersonTypeOptions,
  showDateOfBirth = true,
  controllingPersonTypeLabel = "Controlling person type",
}: ControllingPersonsFormProps) => {
  const { values, setFieldTouched } = useFormContext<TaxFormValues>();
  const [isSaving, setIsSaving] = useState(false);

  const entries = (values[fieldName] as ControllingPersonEntry[]) || [];
  const currentEntry = entries[index];
  const basePath = `${fieldName}.${index}`;

  const fieldConfigs = useMemo((): Record<string, FormFieldConfig> => {
    return {
      firstName: {
        name: `${basePath}.firstName`,
        type: "text",
        label: "",
        placeholder: "First",
      },
      middleName: {
        name: `${basePath}.middleName`,
        type: "text",
        label: "",
        placeholder: "Middle",
      },
      lastName: {
        name: `${basePath}.lastName`,
        type: "text",
        label: "",
        placeholder: "Last",
      },
      dateOfBirth: {
        name: `${basePath}.dateOfBirth`,
        type: "date",
        label: "Date of birth",
        placeholder: "MM/DD/YYYY",
      },
      controllingPersonType: {
        name: `${basePath}.controllingPersonType`,
        type: "select",
        label: controllingPersonTypeLabel,
        placeholder: "Select a type",
        options: controllingPersonTypeOptions,
      },
      tin: {
        name: `${basePath}.tin`,
        type: "text",
        label: "TIN",
        placeholder: "",
      },
      tinNotRequired: {
        name: `${basePath}.tinNotRequired`,
        type: "checkbox",
        label: "Check if TIN not legally required",
      },
    };
  }, [basePath, controllingPersonTypeLabel, controllingPersonTypeOptions]);

  const addressFields = useMemo(() => createAddressFields(`${basePath}.address`), [basePath]);

  const handleSubmit = useCallback(async () => {
    void setFieldTouched(`${basePath}.firstName`, true, true);
    void setFieldTouched(`${basePath}.lastName`, true, true);

    if (!currentEntry?.firstName || !currentEntry?.lastName) return;

    setIsSaving(true);
    try {
      await onSubmit();
    } finally {
      setIsSaving(false);
    }
  }, [basePath, currentEntry, onSubmit, setFieldTouched]);

  return (
    <div className={styles.formContainer}>
      <Flex flexDirection="column" gap={Sizes.MD}>
        <Text className={styles.formInfoText}>{infoText}</Text>

        <Flex flexDirection="column" gap={Sizes.XS}>
          <Label>Name</Label>
          <Flex gap={Sizes.MD} className={styles.nameRow}>
            <div className={styles.nameField}>
              {FIELD_RENDERERS.text?.(fieldConfigs.firstName, "")}
            </div>
            <div className={styles.nameField}>
              {FIELD_RENDERERS.text?.(fieldConfigs.middleName, "")}
            </div>
            <div className={styles.nameField}>
              {FIELD_RENDERERS.text?.(fieldConfigs.lastName, "")}
            </div>
          </Flex>
        </Flex>

        {showDateOfBirth && (
          <div className={styles.halfWidthField}>
            {FIELD_RENDERERS.date?.(fieldConfigs.dateOfBirth, "Date of birth")}
          </div>
        )}

        {controllingPersonTypeOptions && controllingPersonTypeOptions.length > 0 && (
          <div className={styles.halfWidthField}>
            {FIELD_RENDERERS.select?.(
              fieldConfigs.controllingPersonType,
              controllingPersonTypeLabel,
            )}
          </div>
        )}

        <Flex flexDirection="column" gap={Sizes.XS}>
          <Label>Residence address</Label>
          <Flex flexDirection="column" gap={Sizes.SM}>
            {addressFields.map((field) => {
              const renderer = FIELD_RENDERERS[field.type];
              if (!renderer) return null;

              if (field.gridColumn === "half") {
                return null;
              }

              return <div key={field.name}>{renderer(field, "")}</div>;
            })}
            <Flex gap={Sizes.MD}>
              {addressFields
                .filter((field) => field.gridColumn === "half")
                .map((field) => (
                  <div key={field.name} className={styles.nameField}>
                    {FIELD_RENDERERS.text?.(field, "")}
                  </div>
                ))}
            </Flex>
          </Flex>
        </Flex>

        <div className={styles.halfWidthField}>
          {FIELD_RENDERERS.text?.(fieldConfigs.tin, "TIN")}
        </div>

        {FIELD_RENDERERS.checkbox?.(
          fieldConfigs.tinNotRequired,
          "Check if TIN not legally required",
        )}
      </Flex>

      <Flex gap={Sizes.SM} className={styles.formButtonsContainer}>
        <Button
          onClick={() => void handleSubmit()}
          disabled={isSaving}
          data-testid="controlling-person-submit"
        >
          {isNewEntry ? addButtonText : updateButtonText}
        </Button>
        <Button
          variant="neutral"
          onClick={onCancel}
          disabled={isSaving}
          data-testid="controlling-person-cancel"
        >
          Cancel
        </Button>
      </Flex>
    </div>
  );
};
